// Session Timeout Hook for Security Compliance
import { useEffect, useRef, useCallback } from 'react';
import { useAuth } from './useAuth';
import { auditService } from '../services/auditService';

export const useSessionTimeout = (timeoutMinutes = 30, warningMinutes = 5) => {
  const { user, signOut } = useAuth();
  const timeoutRef = useRef(null);
  const warningRef = useRef(null);
  const lastActivityRef = useRef(Date.now());

  const handleTimeout = useCallback(async () => {
    if (user) {
      // Log the timeout event
      await auditService.logAuthEvent('session_timeout', {
        email: user.email,
        reason: 'inactivity',
        timeout_minutes: timeoutMinutes
      }, false);

      // Sign out the user
      await signOut();
      
      // Redirect to login with message
      window.location.href = '/admin?timeout=true';
    }
  }, [user, signOut, timeoutMinutes]);

  const showWarning = useCallback(() => {
    if (!document.getElementById('session-warning')) {
      const warning = document.createElement('div');
      warning.id = 'session-warning';
      warning.className = 'fixed top-4 right-4 bg-yellow-50 border border-yellow-200 text-yellow-800 px-6 py-4 rounded-lg shadow-lg z-50 max-w-sm';
      warning.innerHTML = `
        <div class="flex items-start space-x-3">
          <svg class="w-5 h-5 text-yellow-600 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"></path>
          </svg>
          <div>
            <p class="font-medium">Session Expiring Soon</p>
            <p class="text-sm mt-1">Your session will expire in ${warningMinutes} minutes due to inactivity.</p>
            <button onclick="document.getElementById('session-warning').remove()" class="text-sm text-yellow-600 hover:text-yellow-700 mt-2 font-medium">
              Continue Working
            </button>
          </div>
        </div>
      `;
      document.body.appendChild(warning);
      
      // Auto-remove after 10 seconds
      setTimeout(() => {
        const el = document.getElementById('session-warning');
        if (el) el.remove();
      }, 10000);
    }
  }, [warningMinutes]);

  const resetTimeout = useCallback(() => {
    // Clear existing timers
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    if (warningRef.current) clearTimeout(warningRef.current);

    if (!user) return;

    lastActivityRef.current = Date.now();
    
    // Set warning timer
    warningRef.current = setTimeout(() => {
      showWarning();
    }, (timeoutMinutes - warningMinutes) * 60 * 1000);

    // Set timeout timer
    timeoutRef.current = setTimeout(() => {
      handleTimeout();
    }, timeoutMinutes * 60 * 1000);
  }, [user, timeoutMinutes, warningMinutes, showWarning, handleTimeout]);

  // Track user activity
  useEffect(() => {
    if (!user) return;

    const events = ['mousedown', 'keydown', 'scroll', 'touchstart', 'click'];
    
    const handleActivity = () => {
      const now = Date.now();
      // Only reset if more than 1 minute has passed since last activity
      if (now - lastActivityRef.current > 60000) {
        resetTimeout();
      }
    };

    // Attach listeners
    events.forEach(event => {
      document.addEventListener(event, handleActivity);
    });

    // Initial timeout setup
    resetTimeout();

    // Check session validity every minute
    const intervalId = setInterval(async () => {
      if (user?.session?.access_token) {
        const isValid = await auditService.checkSessionTimeout(
          user.session.access_token,
          timeoutMinutes
        );
        if (!isValid) {
          handleTimeout();
        }
      }
    }, 60000);

    return () => {
      // Cleanup
      events.forEach(event => {
        document.removeEventListener(event, handleActivity);
      });
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (warningRef.current) clearTimeout(warningRef.current);
      clearInterval(intervalId);
    };
  }, [user, resetTimeout, handleTimeout, timeoutMinutes]);

  return {
    resetTimeout,
    lastActivity: lastActivityRef.current
  };
};
